/**
 * Accessibility fixes for controls rendered and re-rendered by Nosto on PLPs.
 *
 * Nosto's facet checkboxes do not always respond to their native keyboard
 * activation. It also renders selected-filter chips such as "US Size: 5" as
 * <div href="..."> elements, which leaves them completely outside the tab
 * order. The theme cannot correct that remote markup at render time, so this
 * file progressively enhances each Nosto render and delegates activation.
 *
 * It also covers two related, silent-to-AT gaps in the same sidebar: facet
 * groups (and Sort) are wrapped in role="menu" around plain checkboxes/links,
 * which is not a valid menu structure; and applying a filter re-renders the
 * result count with no announcement at all (WCAG 4.1.3).
 */
(function () {
  'use strict';

  var FACET_CHECKBOX_SELECTOR = '[data-nosto-element="facet-setting"] input[type="checkbox"]';
  var ACTIVE_FILTER_SELECTOR = 'nav.ns-header-controls .active__filters__remove[data-filter-update-url]';
  var DISRUPTOR_SELECTOR = '.ns-disruptor.product-item--featured-image';
  var DISRUPTOR_LINK_SELECTOR = '.product-item__image-link[href]';
  var NOSTO_NAV_SELECTOR = 'nav.ns-header-controls, nav.pagination-custom';
  var SIDEBAR_MENU_SELECTOR = '.collection__sidebar__group [role="menu"]';
  var RESULT_COUNT_SELECTOR = 'nav.ns-header-controls .collection_products_count';
  var USER_INTERACTION_SELECTOR = '.collection__sidebar__group, ' + ACTIVE_FILTER_SELECTOR;
  var ANNOUNCE_DEBOUNCE = 400;
  var scheduledDisruptors = new WeakSet();

  // Set true by a facet/sort/remove-filter interaction, consumed by the next
  // settled re-render. Keeps pagination and other unrelated re-renders silent.
  var userChangedFilters = false;
  var lastAnnouncedCount = null;
  var announceTimer = null;

  function isActivationKey(e) {
    return e.key === 'Enter' || e.code === 'Enter' || e.key === ' ' || e.code === 'Space';
  }

  function enhanceActiveFilter(control) {
    control.setAttribute('tabindex', '0');
    control.setAttribute('role', 'button');

    var filterName = control.querySelector('small');
    var label = filterName ? filterName.textContent.trim() : control.textContent.trim();
    if (label) control.setAttribute('aria-label', 'Remove filter: ' + label);
  }

  function labelFacetCheckbox(checkbox) {
    if ((checkbox.getAttribute('aria-label') || '').trim()) return;
    var option = checkbox.parentElement.querySelector('label');
    var optionName = option && (option.textContent.trim() || option.querySelector('[data-swatch]')?.getAttribute('data-swatch'));
    var group = checkbox.closest('.collection__sidebar__group');
    var heading = group && group.querySelector('.collection__sidebar__heading span');
    var groupName = heading && heading.textContent.trim();
    if (optionName) checkbox.setAttribute('aria-label', (groupName ? groupName + ': ' : '') + optionName);
  }

  function labelNostoNavigation(nav) {
    if ((nav.getAttribute('aria-label') || '').trim()) return;
    nav.setAttribute('aria-label', nav.matches('.pagination-custom') ? 'Product pages' : 'Filter and sort products');
  }

  function relabelSidebarMenu(menu) {
    if (menu.getAttribute('role') !== 'menu') return;
    menu.setAttribute('role', 'group');
    var group = menu.closest('.collection__sidebar__group');
    var heading = group && group.querySelector('.collection__sidebar__heading span');
    var label = heading && heading.textContent.trim();
    if (label) menu.setAttribute('aria-label', label);
  }

  function markUserChangedFilters(e) {
    var target = e.target;
    if (target && target.closest && target.closest(USER_INTERACTION_SELECTOR)) {
      userChangedFilters = true;
    }
  }

  function resultCountMessage() {
    var el = document.querySelector(RESULT_COUNT_SELECTOR);
    if (!el) return null;
    var text = el.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return null;
    return /^0\b/.test(text) ? 'No products match the selected filters.' : text + '.';
  }

  function scheduleResultAnnouncement() {
    window.clearTimeout(announceTimer);
    announceTimer = window.setTimeout(function () {
      if (!userChangedFilters) return;
      var message = resultCountMessage();
      if (!message || message === lastAnnouncedCount) return;
      lastAnnouncedCount = message;
      userChangedFilters = false;
      if (window.themeA11yAnnounce) window.themeA11yAnnounce(message);
    }, ANNOUNCE_DEBOUNCE);
  }

  function labelFromHref(link) {
    try {
      var slug = new URL(link.href, window.location.href).pathname.split('/').filter(Boolean).pop();
      if (!slug) return 'Featured collection';
      var label = slug.replace(/-/g, ' ');
      return label.charAt(0).toUpperCase() + label.slice(1);
    } catch (error) {
      return 'Featured collection';
    }
  }

  function alignDisruptorWithVisualOrder(disruptor) {
    if (!disruptor.isConnected || !disruptor.parentElement) return;

    var grid = disruptor.parentElement;
    var tiles = Array.prototype.filter.call(grid.children, function (child) {
      return child.matches('.product-item') && child.getClientRects().length;
    });
    if (tiles.length < 2) return;

    var visualOrder = tiles.slice().sort(function (first, second) {
      var firstRect = first.getBoundingClientRect();
      var secondRect = second.getBoundingClientRect();
      return Math.abs(firstRect.top - secondRect.top) > 5
        ? firstRect.top - secondRect.top
        : firstRect.left - secondRect.left;
    });
    var visualIndex = visualOrder.indexOf(disruptor);
    if (visualIndex < 0) return;

    var nextVisualTile = visualOrder[visualIndex + 1];
    if (nextVisualTile && disruptor.nextElementSibling !== nextVisualTile) {
      grid.insertBefore(disruptor, nextVisualTile);
    } else if (!nextVisualTile && grid.lastElementChild !== disruptor) {
      grid.appendChild(disruptor);
    }
  }

  function enhanceDisruptor(disruptor) {
    var link = disruptor.querySelector(DISRUPTOR_LINK_SELECTOR);
    if (!link) return;

    link.setAttribute('tabindex', '0');
    if (!(link.getAttribute('aria-label') || '').trim()) {
      link.setAttribute('aria-label', labelFromHref(link));
    }

    if (scheduledDisruptors.has(disruptor)) return;
    scheduledDisruptors.add(disruptor);
    window.requestAnimationFrame(function () {
      alignDisruptorWithVisualOrder(disruptor);
      window.setTimeout(function () {
        alignDisruptorWithVisualOrder(disruptor);
        scheduledDisruptors.delete(disruptor);
      }, 250);
    });
  }

  function scan(root) {
    if (!root.querySelectorAll) return;
    if (root.nodeType === 1 && root.matches(ACTIVE_FILTER_SELECTOR)) enhanceActiveFilter(root);
    if (root.nodeType === 1 && root.matches(DISRUPTOR_SELECTOR)) enhanceDisruptor(root);
    if (root.nodeType === 1 && root.matches(NOSTO_NAV_SELECTOR)) labelNostoNavigation(root);
    if (root.nodeType === 1 && root.matches(FACET_CHECKBOX_SELECTOR)) labelFacetCheckbox(root);
    if (root.nodeType === 1 && root.matches(SIDEBAR_MENU_SELECTOR)) relabelSidebarMenu(root);
    root.querySelectorAll(ACTIVE_FILTER_SELECTOR).forEach(enhanceActiveFilter);
    root.querySelectorAll(DISRUPTOR_SELECTOR).forEach(enhanceDisruptor);
    root.querySelectorAll(NOSTO_NAV_SELECTOR).forEach(labelNostoNavigation);
    root.querySelectorAll(FACET_CHECKBOX_SELECTOR).forEach(labelFacetCheckbox);
    root.querySelectorAll(SIDEBAR_MENU_SELECTOR).forEach(relabelSidebarMenu);
  }

  function onKeydown(e) {
    if (e.repeat) return;
    if (!isActivationKey(e)) return;

    const target = e.target;
    if (!target || !target.matches) return;

    if (target.matches(ACTIVE_FILTER_SELECTOR)) {
      var removeControl = target.querySelector('.filter__x');
      if (!removeControl) return;

      e.preventDefault();
      e.stopPropagation();
      // Match the pointer interaction exactly: Nosto listens for activation
      // on the chip's × control and removes only that individual filter.
      removeControl.click();
      return;
    }

    if (!target.matches(FACET_CHECKBOX_SELECTOR)) return;

    e.preventDefault();
    target.click();
  }

  scan(document);

  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(scan);
    });
    scheduleResultAnnouncement();
  }).observe(document.body, { childList: true, subtree: true });

  // Listen before Nosto's handlers so they cannot swallow keyboard input from
  // either the facet checkbox or the selected-filter removal chip.
  window.addEventListener('keydown', onKeydown, true);

  // Capture phase so a click that also gets removed/replaced by the
  // subsequent Nosto re-render is still seen before it disappears.
  document.addEventListener('pointerdown', markUserChangedFilters, true);
  document.addEventListener('keydown', markUserChangedFilters, true);

  window.addEventListener('resize', function () {
    document.querySelectorAll(DISRUPTOR_SELECTOR).forEach(enhanceDisruptor);
  });
})();
